import type { HttpErrorResponse } from '@angular/common/http';

import { type EntityState, ngrxEvent, type PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@core/ngrx-legacy';
import { stableStringify } from '@core/utils-legacy';
import type {
  DataSourceId,
  FetchRecordsPayload,
  GroupParams,
  LayoutRecordsCountPayload,
  AnyGroup,
  LayoutRecordsParams,
  RecordsState,
  ViewRecord,
  GroupId,
} from '@core/views-types';

export interface RecordsQueryParams {
  limit: number;
  page: number;
}

export interface RecordsFetchStoreOptions {
  concat?: boolean;
  customId?: string;
}

export const getRecordsId = (
  dataSourceId: string,
  dataScope: 'default' | string,
  groupParams?: GroupParams | null,
  swimlaneParams?: LayoutRecordsParams | null,
) => {
  const swimlane = swimlaneParams?.type === 'board' ? swimlaneParams.swimlane : null;
  return [
    dataSourceId,
    dataScope,
    groupParams?.configId,
    stableStringify(groupParams?.value),
    swimlane?.configId,
    stableStringify(swimlane?.value),
  ].join(':');
};
const splitRecordsId = (recordsId: string) =>
  <
    [
      dataSource: string,
      dataScope: string,
      groupingId: string,
      groupId: string,
      swimlaneGroupingId: string,
      swimlaneId: string,
    ]
  >recordsId.split(':');
const idSelector: any = (entity: { id: number }) => entity.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<RecordsState>()(
  'ViewRecords',
  {
    idSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      const fetch = (
        state: EntityState<RecordsState>,
        payload: {
          recordsId: string;
          dataSourceId: string;
          data: FetchRecordsPayload;
          queryParams?: RecordsQueryParams;
          storeOptions?: RecordsFetchStoreOptions;
        },
      ) =>
        adapter.markAsPending(state, {
          scope: null,
          entities: [payload.recordsId],
        });

      return {
        fetch,
        fetchSuccess: (state, payload: { data: RecordsState; concat: boolean }) => {
          const currentData = state.entityMap[payload.data.id];
          const data = payload.concat
            ? {
                ...payload.data,
                records: [...(currentData?.records ?? []), ...payload.data.records],
              }
            : {
                ...payload.data,
              };
          return defaultActions.fetchOneSuccess(state, { data });
        },
        fetchFailed: (state, payload: { error: HttpErrorResponse; id: string }) =>
          defaultActions.fetchOneFailed(state, payload),

        fetchAll: (
          state: EntityState<RecordsState>,
          payload: {
            recordsId: string;
            dataSourceId: DataSourceId;
            data: FetchRecordsPayload;
            uuid: string;
          },
        ) => state,
        fetchAllSuccess: (state, payload: { data: RecordsState; uuid: string }) =>
          defaultActions.fetchOneSuccess(state, payload),
        fetchAllFailed: (state, payload: { id: string; uuid: string; error: HttpErrorResponse }) =>
          defaultActions.fetchOneFailed(state, payload),

        refresh: (
          state,
          payload: {
            recordsId: string;
            dataSourceId: string;
            meta: RecordsState['meta'];
            data: FetchRecordsPayload;
          },
        ) => state,
        refreshSuccess: (state, payload: { data: RecordsState }) =>
          defaultActions.fetchOneSuccess(state, payload),
        refreshFailed: (
          state,
          payload: { recordsId: string; dataSourceId: string; data: FetchRecordsPayload },
        ) => state,

        fetchViewAllRecordsCount: (
          state,
          payload: { dataSourceId: string; recordsId: string; data: LayoutRecordsCountPayload },
        ) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.recordsId),
            next =>
              adapter.markAsPending(next, {
                scope: null,
                entities: [payload.recordsId],
              }),
          ),
        fetchViewAllRecordsCountSuccess: defaultActions.fetchOneSuccess,
        fetchViewAllRecordsCountFailed: defaultActions.fetchOneFailed,

        moveRecord: ngrxEvent<{
          dataSourceId: string;
          recordId: ViewRecord['id'];
          targetGroup: { configId: string; groupId: GroupId };
        }>(),
        moveRecordSuccess: ngrxEvent(),
        moveRecordFailed: ngrxEvent(),

        clearById: (state, payload: { recordsId: string }) =>
          adapter.removeOne(state, payload.recordsId),
        clear: (
          state,
          payload: { dataSourceId: string; dataScope: string; group?: AnyGroup | null },
        ) => {
          const recordsIds = Object.keys(state.entityMetaMap).filter(id => {
            const [dataSourceId, dataScope, _, groupId] = splitRecordsId(id);
            const sourceScopeMatch =
              dataSourceId === payload.dataSourceId && dataScope === payload.dataScope;
            if (payload.group === undefined) return sourceScopeMatch;

            return sourceScopeMatch && groupId === stableStringify(payload.group?.id);
          });
          return adapter.removeMany(state, recordsIds);
        },
      };
    },
  },
);
export const ViewRecordsActions: PickActions<
  typeof actions,
  'fetch' | 'fetchAll' | 'fetchViewAllRecordsCount' | 'refresh' | 'moveRecord'
> &
  Pick<typeof actions, 'clear' | 'clearById'> = actions;
export {
  reducer as ViewRecordsReducer,
  namespace as ViewRecordsNamespace,
  selectors as ViewRecordsSelectors,
};
